import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { LancamentoFinanceiro } from '../../extrato-movimentacoes.component';
import { MovimentacaoService } from '../../../../../shared/services/apis/financas/movimentacao.service';
import { AcaoTagsEnum } from '../../../../../shared/enums/financas/acao-tags.enum';
import { TagMovimentacao } from '../../../../../shared/services/models/financas/tag-movimentacao.model';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';

export interface ModalTagsResult {
  success: boolean;
}

@Component({
  selector: 'app-modal-tags',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatInputModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './modal-tags.component.html',
  styleUrls: ['./modal-tags.component.css'],
})
export class ModalTagsComponent implements OnInit {
  public modo: string = 'incluir';
  public searchText: string = '';
  public tagsSelecionados: Set<number> = new Set();
  public tagOptions: TagMovimentacao[] = [];
  public tagsFiltrados: TagMovimentacao[] = [];
  public isLoading: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ModalTagsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { lancamentos: LancamentoFinanceiro[] },
    private movimentacaoService: MovimentacaoService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.carregarTags();
  }

  private carregarTags(): void {
    this.isLoading = true;
    this.movimentacaoService.getLookup().subscribe({
      next: response => {
        this.isLoading = false;
        if (!response.isError) {
          this.tagOptions = response.listaTagsMovimentacao || [];
          this.tagsFiltrados = [...this.tagOptions];
        }
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  public filtrarTags(): void {
    const termo = this.searchText.toLowerCase().trim();
    if (!termo) {
      this.tagsFiltrados = [...this.tagOptions];
    } else {
      this.tagsFiltrados = this.tagOptions.filter(m => m.descricao.toLowerCase().includes(termo));
    }
  }

  public toggleTag(id: number): void {
    if (this.tagsSelecionados.has(id)) {
      this.tagsSelecionados.delete(id);
    } else {
      this.tagsSelecionados.add(id);
    }
  }

  public isTagSelecionado(id: number): boolean {
    return this.tagsSelecionados.has(id);
  }

  public get contadorText(): string {
    const count = this.tagsSelecionados.size;
    if (count === 0) return '';
    return count === 1 ? '1 tag selecionado' : `${count} tags selecionados`;
  }

  public get helpText(): string {
    if (this.modo === 'incluir') {
      return 'Os tags selecionados serão adicionados aos lançamentos, mantendo os existentes.';
    }
    return 'Os tags selecionados serão removidos dos lançamentos, mantendo os demais existentes.';
  }

  public get podeAplicar(): boolean {
    return this.tagsSelecionados.size > 0 && !this.isLoading;
  }

  public onModoChange(): void {
    this.tagsSelecionados.clear();
  }

  public salvar(): void {
    if (!this.podeAplicar) return;

    this.isLoading = true;
    const ids = this.data.lancamentos.map(l => l.id);
    const acao = this.modo === 'incluir' ? AcaoTagsEnum.Incluir : AcaoTagsEnum.Remover;

    this.movimentacaoService.bulkUpdateTags(ids, acao, Array.from(this.tagsSelecionados)).subscribe({
      next: response => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription || 'Erro ao atualizar tags.', AlertType.error);
          return;
        }
        const mensagem = this.modo === 'incluir' ? 'Tags incluídos com sucesso!' : 'Tags removidos com sucesso!';
        this.alertService.show('Sucesso', mensagem, AlertType.success);
        this.dialogRef.close({ success: true } as ModalTagsResult);
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao atualizar tags.', AlertType.error);
      },
    });
  }

  public fechar(): void {
    this.dialogRef.close();
  }
}
